import { ActionTree } from 'vuex';
import { InquiryFormState, RootState } from '../types';
import { state as authState } from '../auth/auth';
import { AxiosInstance } from 'axios';
import { handleAllErrors } from '@/utils/errorHandling';
import { decodeInquiryForm, decodeInquiryFormRequest } from '@/utils/decoding';

const PAGE_LIMIT = 15;

export const actions: ActionTree<InquiryFormState, RootState> = {

	/**
	 * Fetch the inquiry form
	 * If no inquiry form exists, then a new one will be created
	 *
	 * @return {Promise<void>}
	 */
	async fetchInquiryForm({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const { data } = await axiosInst.get<InquiryForm>('/inquiry-forms');
			commit('SET_INQUIRY_FORM', decodeInquiryForm(data));
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update the existing inquiry form
	 *
	 * @param {InquiryForm} form
	 * @return {Promise<void>}
	 */
	async updateInquiryForm({ commit }, form: InquiryForm): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const { data } = await axiosInst.put<InquiryForm>(`/inquiry-forms/${form.id}`, form);
			commit('SET_INQUIRY_FORM', decodeInquiryForm(data));
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch inquiry form requests using the current filters and pagination from state.
	 *
	 * @return {Promise<void>}
	 */
	async fetchInquiryFormRequests({ commit, state }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const params = Object.fromEntries(Object.entries({ ...state.filters, page: state.currentPage, limit: PAGE_LIMIT }).filter(([, value]) => value !== '' && value !== null));

		try {
			const { data } = await axiosInst.get<PaginatedResults<InquiryFormRequest>>('/inquiry-forms/requests', { params });
			const decodedRows = data.rows.map(decodeInquiryFormRequest);
			commit('SET_INQUIRY_FORM_REQUESTS', decodedRows);
			commit('SET_TOTAL_PAGES', data.count === 0 ? 1 : Math.ceil(data.count / PAGE_LIMIT));
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch a single inquiry form request by ID and set it as the selected request.
	 *
	 * @param {number} inquiryRequestId
	 * @return {Promise<void>}
	 */
	async fetchInquiryFormRequestById({ commit }, inquiryRequestId: number): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const { data } = await axiosInst.get<InquiryFormRequest>(`/inquiry-forms/requests/${inquiryRequestId}`);
			commit('SET_SELECTED_INQUIRY_FORM_REQUEST', decodeInquiryFormRequest(data));
			commit('UPDATE_INQUIRY_FORM_REQUEST', decodeInquiryFormRequest(data));
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Set the currently selected inquiry form request.
	 *
	 * @param {InquiryFormRequest | null} request
	 * @return {void}
	 */
	setSelectedInquiryFormRequest({ commit }, request: InquiryFormRequest | null): void {
		commit('SET_SELECTED_INQUIRY_FORM_REQUEST', request);
	},

	/**
	 * Set the active inquiry form filters.
	 *
	 * @param {InquiryFormFilters} filters
	 * @return {void}
	 */
	setInquiryFormFilters({ commit }, filters: InquiryFormFilters): void {
		commit('SET_INQUIRY_FORM_FILTERS', filters);
	},

	/**
	 * Set the current page for inquiry form requests.
	 *
	 * @param {number} page
	 * @return {void}
	 */
	setCurrentPage({ commit }, page: number): void {
		commit('SET_CURRENT_PAGE', page);
	},

	async updateInquiryFormRequests({ commit }, request: InquiryFormRequest): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const { data } = await axiosInst.put<InquiryFormRequest>(`/inquiry-forms/requests/${request.id}`, request);
			commit('UPDATE_INQUIRY_FORM_REQUEST', decodeInquiryFormRequest(data));
		}
		catch (error) {
			handleAllErrors(error);
		}
	}
};
